/**
 * Folder destination selector component
 *
 * Allows users to choose where imported cards should be placed:
 * - Collection Root (no folder)
 * - Specific folder from their collection
 *
 * Also has a toggle for "Use folder structure from import" which:
 * - When checked: creates folders from import data under selected destination
 * - When unchecked: all cards go directly to selected destination
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { IoFolder, IoHome } from 'react-icons/io5';

import type { Folder } from '@/types/card';

// ============================================================================
// Types
// ============================================================================

export type FolderDestination =
  | { type: 'root' } // Collection root (no parent)
  | { type: 'folder'; folderId: string }; // Specific folder

export interface FolderDestinationSelectorProps {
  /** Currently selected destination */
  value: FolderDestination;
  /** Called when destination changes */
  onChange: (destination: FolderDestination) => void;
  /** Whether to use folder structure from import */
  useImportFolders: boolean;
  /** Called when folder structure toggle changes */
  onUseImportFoldersChange: (use: boolean) => void;
  /** Whether the import has folder data */
  hasImportFolders: boolean;
  /** User's existing folders */
  folders: Folder[];
  /** Additional className */
  className?: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Build a flat list of folders with indentation for hierarchy
 */
function buildFolderTree(folders: Folder[]): Array<{ folder: Folder; depth: number }> {
  const result: Array<{ folder: Folder; depth: number }> = [];

  // First, find root folders (parent_id is null)
  const rootFolders = folders.filter((f) => !f.parent_id);

  // Recursively add folders with depth tracking
  function addFolderWithChildren(folder: Folder, depth: number) {
    result.push({ folder, depth });
    // Find children
    const children = folders.filter((f) => f.parent_id === folder.id);
    children.sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
    for (const child of children) {
      addFolderWithChildren(child, depth + 1);
    }
  }

  rootFolders.sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
  for (const folder of rootFolders) {
    addFolderWithChildren(folder, 0);
  }

  return result;
}

/**
 * Get display name for selected destination
 */
function getDestinationLabel(destination: FolderDestination, folders: Folder[]): string {
  switch (destination.type) {
    case 'root':
      return 'Collection Root';
    case 'folder':
      const folder = folders.find((f) => f.id === destination.folderId);
      return folder?.name ?? 'Unknown folder';
  }
}

// ============================================================================
// Component
// ============================================================================

export default function FolderDestinationSelector({
  value,
  onChange,
  useImportFolders,
  onUseImportFoldersChange,
  hasImportFolders,
  folders,
  className,
}: FolderDestinationSelectorProps): ReactElement {
  const folderTree = useMemo(() => buildFolderTree(folders), [folders]);

  const handleDestinationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    if (val === 'root') {
      onChange({ type: 'root' });
    } else {
      onChange({ type: 'folder', folderId: val });
    }
  };

  const selectValue = value.type === 'folder' ? value.folderId : value.type;

  return (
    <div className={clsx('space-y-3', className)}>
      <h4 className="text-pearl text-sm font-medium">Import Destination</h4>

      {/* Folder dropdown */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <select
            id="folder-destination"
            value={selectValue}
            onChange={handleDestinationChange}
            className="bg-slate border-storm text-pearl focus-ring w-full appearance-none rounded-lg border py-2 pr-10 pl-10 text-sm"
            aria-label="Select import destination folder"
          >
            <option value="root">Collection Root</option>
            {folderTree.length > 0 && (
              <optgroup label="Your Folders">
                {folderTree.map(({ folder, depth }) => (
                  <option key={folder.id} value={folder.id}>
                    {'  '.repeat(depth)}
                    {folder.name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          <div className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2">
            {value.type === 'root' ? <IoHome className="text-ash h-4 w-4" /> : <IoFolder className="text-aether h-4 w-4" />}
          </div>
          <div className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2">
            <svg className="text-ash h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Use import folder structure toggle */}
      {hasImportFolders && (
        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            checked={useImportFolders}
            onChange={(e) => onUseImportFoldersChange(e.target.checked)}
            className="border-storm bg-slate focus-ring mt-0.5 h-4 w-4 rounded"
          />
          <div>
            <span className="text-pearl text-sm font-medium">Use folder structure from import</span>
            <p className="text-ash mt-0.5 text-xs">
              {useImportFolders
                ? `Folders from the import will be created under "${getDestinationLabel(value, folders)}"`
                : 'All cards will go directly to the selected destination'}
            </p>
          </div>
        </label>
      )}

      {/* No folders in import note */}
      {!hasImportFolders && (
        <p className="text-ash text-xs">
          This import doesn't contain folder information. All cards will be placed in the selected destination.
        </p>
      )}
    </div>
  );
}
